import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  ChartNoAxesColumn,
  Clock,
  Ellipsis,
  Funnel,
  LayoutGrid,
  ListTodo,
} from "lucide-react";
import { useCanAccess, useTranslate } from "ra-core";
import { Link, matchPath, useLocation } from "react-router";

export const MobileNavigation = () => {
  const location = useLocation();
  const translate = useTranslate();
  const { canAccess: seesDashboard } = useCanAccess({
    resource: "dashboard",
    action: "list",
  });

  let currentPath: string | boolean = "/";
  if (matchPath("/settings", location.pathname)) {
    currentPath = "/settings";
  } else if (matchPath("/apartments", location.pathname)) {
    currentPath = "/apartments";
  } else if (matchPath("/today", location.pathname)) {
    currentPath = "/today";
  } else if (matchPath("/", location.pathname)) {
    currentPath = "/";
  } else if (matchPath("/funnel/*", location.pathname)) {
    currentPath = "/funnel";
  } else if (matchPath("/contacts/*", location.pathname)) {
    currentPath = "/contacts";
  } else if (matchPath("/companies/*", location.pathname)) {
    currentPath = "/companies";
  } else if (matchPath("/tasks/*", location.pathname)) {
    currentPath = "/tasks";
  } else if (matchPath("/deals/*", location.pathname)) {
    currentPath = "/deals";
  } else {
    currentPath = false;
  }

  // Check if the app is running as a PWA (standalone mode)
  const isPwa = window.matchMedia("(display-mode: standalone)").matches;
  // Check if it's iOS on the web
  const isWebiOS = /iPad|iPod|iPhone/.test(window.navigator.userAgent);

  return (
    <nav
      aria-label={translate("crm.navigation.label")}
      className="fixed bottom-0 left-0 right-0 z-50 bg-secondary h-14"
      style={{
        // iOS bug: even though viewport is set correctly, the bottom safe area inset is not accounted for
        // So we manually add some padding to avoid the navigation being too close to the home bar
        paddingBottom: isPwa && isWebiOS ? 15 : undefined,
        // We use box-sizing: border-box, so the height contains the padding.
        // To actually increase the padding, we need to increase the height as well
        height:
          "calc(var(--spacing)) * 6" + (isPwa && isWebiOS ? " + 15px" : ""),
      }}
    >
      <div className="flex justify-center">
        {/* Owner (docs/design/DashboardM): Dashboard, Pipeline, Apartments,
            More. Manager (Main): Today, Pipeline, Tasks, Apartments. */}
        {seesDashboard ? (
          <NavigationButton
            href="/"
            Icon={ChartNoAxesColumn}
            label={translate("ra.page.dashboard")}
            isActive={currentPath === "/"}
          />
        ) : (
          <NavigationButton
            href="/today"
            Icon={Clock}
            label={translate("crm.navigation.today")}
            isActive={currentPath === "/today"}
          />
        )}
        <NavigationButton
          href="/funnel"
          Icon={Funnel}
          label={translate("crm.navigation.pipeline")}
          isActive={currentPath === "/funnel"}
        />
        {seesDashboard ? null : (
          <NavigationButton
            href="/tasks"
            Icon={ListTodo}
            label={translate("crm.navigation.tasks")}
            isActive={currentPath === "/tasks"}
          />
        )}
        <NavigationButton
          href="/apartments"
          Icon={LayoutGrid}
          label={translate("crm.navigation.apartments")}
          isActive={currentPath === "/apartments"}
        />
        {seesDashboard ? (
          <NavigationButton
            href="/settings"
            Icon={Ellipsis}
            label={translate("crm.owner_dashboard.more")}
            isActive={currentPath === "/settings"}
          />
        ) : null}
      </div>
    </nav>
  );
};

const NavigationButton = ({
  href,
  Icon,
  label,
  isActive,
}: {
  href: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  isActive: boolean;
}) => (
  <Link
    to={href}
    className={cn(
      buttonVariants({ variant: "ghost" }),
      "flex-col gap-1 h-auto py-2 px-1 rounded-md w-16",
      isActive ? null : "text-muted-foreground",
    )}
  >
    <Icon className="size-6" />
    <span className="text-[0.6rem] font-medium">{label}</span>
  </Link>
);
